import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import LandingPage from '@/pages/LandingPage';
import SchoolLogin from '@/pages/SchoolLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import TeacherDashboard from '@/pages/TeacherDashboard';
import StudentDashboard from '@/pages/StudentDashboard';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SuperAdminLogin from '@/pages/SuperAdminLogin';
import TVDisplay from '@/pages/TVDisplay';
import Messages from '@/pages/Messages';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SchoolSettingsProvider, useSchoolSettings } from '@/context/SchoolSettingsContext';
import '@/App.css';
import '@/styles/mobile.css';

// Protected route that respects school subdomain
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const { subdomain } = useParams();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl">Жүктелуде...</div>
      </div>
    );
  }

  if (!user) {
    // Redirect to school-specific login if subdomain exists
    if (subdomain) {
      return <Navigate to={`/${subdomain}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (subdomain) {
      return <Navigate to={`/${subdomain}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Global protected route (for super-admin and legacy routes)
const GlobalProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl">Жүктелуде...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { isMainDomain, loading: settingsLoading } = useSchoolSettings();
  
  if (settingsLoading) {
    return null;
  }

  return (
    <Routes>
      {/* Main domain shows Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={isMainDomain ? <LandingPage /> : <Login />} />
      
      {/* Super Admin - global route (both paths supported) */}
      <Route path="/superadmin" element={<SuperAdminLogin />} />
      <Route
        path="/superadmin/dashboard/*"
        element={
          <GlobalProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </GlobalProtectedRoute>
        }
      />
      <Route
        path="/super-admin/*"
        element={
          <GlobalProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </GlobalProtectedRoute>
        }
      />
      
      {/* TV Display */}
      <Route path="/tv" element={<TVDisplay />} />
      
      {/* ============================================ */}
      {/* SCHOOL-SPECIFIC ROUTES: /:subdomain/...     */}
      {/* ============================================ */}
      
      {/* School Login */}
      <Route path="/:subdomain" element={<SchoolLogin />} />
      
      {/* School Admin Dashboard */}
      <Route
        path="/:subdomain/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* School Teacher Dashboard */}
      <Route
        path="/:subdomain/teacher/*"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* School Student Dashboard */}
      <Route
        path="/:subdomain/student/*"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* School Messages */}
      <Route
        path="/:subdomain/messages"
        element={
          <ProtectedRoute allowedRoles={['admin', 'teacher', 'student']}>
            <Messages />
          </ProtectedRoute>
        }
      />
      
      {/* ============================================ */}
      {/* LEGACY ROUTES (redirect to school routes)   */}
      {/* ============================================ */}
      <Route path="/admin/*" element={<LegacyRedirect role="admin" />} />
      <Route path="/teacher/*" element={<LegacyRedirect role="teacher" />} />
      <Route path="/student/*" element={<LegacyRedirect role="student" />} />
      <Route path="/messages" element={<LegacyRedirect role="messages" />} />
      <Route path="/school-login" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Component to redirect legacy routes to school-specific routes
function LegacyRedirect({ role }) {
  const { user } = useAuth();
  
  // Get stored subdomain from localStorage
  const storedSubdomain = localStorage.getItem('school_subdomain');
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (storedSubdomain) {
    if (role === 'messages') {
      return <Navigate to={`/${storedSubdomain}/messages`} replace />;
    }
    return <Navigate to={`/${storedSubdomain}/${role}`} replace />;
  }
  
  // No subdomain stored, go to landing
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <SchoolSettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </SchoolSettingsProvider>
  );
}

export default App;
